import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchContactsReportingTree, type ExternalContactRow, type ReportingTreeResponse, type UserDirectoryRow } from "@/lib/contactsDirectoryClient";
import { formatUnknownError } from "./adminContactsDirectoryUtils";
import type { ReportingForestNode } from "./adminContactsDirectoryShared";

type ToastFn = (message: string, variant?: "ok" | "err") => void;

type Params = {
  organizationId: string;
  directoryMode: "contacts" | "general_support" | "add_contact" | "tags" | "org_tree";
  toast: ToastFn;
  userDir: UserDirectoryRow[];
  external: ExternalContactRow[];
  hideAlreadyAssignedSubordinates: boolean;
  subordinateManagerKey: string;
};

export function useAdminContactsReporting({
  organizationId,
  directoryMode,
  toast,
  userDir,
  external,
  hideAlreadyAssignedSubordinates,
  subordinateManagerKey,
}: Params) {
  const [reportingTree, setReportingTree] = useState<ReportingTreeResponse | null>(null);
  const [reportingBusy, setReportingBusy] = useState(false);
  const [lastReportingRefreshAt, setLastReportingRefreshAt] = useState<number | null>(null);

  const refreshReportingTree = useCallback(
    async (showSuccessToast = false) => {
      setReportingBusy(true);
      try {
        setReportingTree(await fetchContactsReportingTree(organizationId));
        setLastReportingRefreshAt(Date.now());
        if (showSuccessToast) toast("Reporting tree refreshed.", "ok");
      } catch (e: unknown) {
        toast(formatUnknownError(e), "err");
      } finally {
        setReportingBusy(false);
      }
    },
    [organizationId, toast]
  );

  useEffect(() => {
    if (directoryMode === "org_tree") {
      void refreshReportingTree(false);
    }
  }, [directoryMode, refreshReportingTree]);

  const reportingNodeByKey = useMemo(() => {
    const m = new Map<string, NonNullable<ReportingTreeResponse["nodes"]>[number]>();
    for (const n of reportingTree?.nodes ?? []) m.set(n.key, n);
    return m;
  }, [reportingTree]);

  const reportingForest = useMemo(() => {
    const raw = reportingTree?.forest;
    if (!Array.isArray(raw)) return [] as ReportingForestNode[];
    const asNode = (value: unknown): ReportingForestNode | null => {
      if (!value || typeof value !== "object") return null;
      const v = value as Record<string, unknown>;
      if (typeof v.key !== "string") return null;
      const children = Array.isArray(v.children) ? v.children.map(asNode).filter(Boolean) : [];
      return { key: v.key, children: children as ReportingForestNode[] };
    };
    return raw.map(asNode).filter(Boolean) as ReportingForestNode[];
  }, [reportingTree]);

  const reportingLevelByKey = useMemo(() => {
    const levelByKey = new Map<string, number>();
    const visit = (node: ReportingForestNode, level: number) => {
      levelByKey.set(node.key, level);
      for (const child of node.children) visit(child, level + 1);
    };
    for (const root of reportingForest) visit(root, 0);
    return levelByKey;
  }, [reportingForest]);

  const userDirectoryByProfileId = useMemo(() => {
    const m = new Map<string, UserDirectoryRow>();
    for (const row of userDir) {
      if (row.profile_id) m.set(row.profile_id, row);
    }
    return m;
  }, [userDir]);

  const externalDirectoryById = useMemo(() => {
    const m = new Map<string, ExternalContactRow>();
    for (const row of external) {
      m.set(row.id, row);
    }
    return m;
  }, [external]);

  const reportingNodeLabel = useCallback(
    (key: string) => {
      const node = reportingNodeByKey.get(key);
      if (!node) return { name: key, title: "", source: "external" as const };
      return {
        name: String(node.display_name || node.key).trim() || node.key,
        title: String(node.title || "").trim(),
        source: node.source,
      };
    },
    [reportingNodeByKey]
  );

  const reportingPickerLabel = useCallback(
    (node: NonNullable<ReportingTreeResponse["nodes"]>[number]) => {
      let name = String(node.display_name || "").trim();
      let email = "";
      if (node.source === "user" && node.profile_id) {
        const userRow = userDirectoryByProfileId.get(node.profile_id);
        const profile = userRow?.profiles;
        name = name || String(profile?.full_name || profile?.display_name || "").trim();
        email = String(profile?.email || "").trim();
      } else if (node.source === "external" && node.row_id) {
        const extRow = externalDirectoryById.get(node.row_id);
        name = name || String(extRow?.name || "").trim();
        email = String(extRow?.email || "").trim();
      }
      if (!name) name = node.key;
      return email ? `${name} - ${email}` : name;
    },
    [externalDirectoryById, userDirectoryByProfileId]
  );

  const sortedReportingNodes = useMemo(() => {
    return [...(reportingTree?.nodes ?? [])].sort((a, b) => {
      const levelCmp = (reportingLevelByKey.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
        (reportingLevelByKey.get(b.key) ?? Number.MAX_SAFE_INTEGER);
      if (levelCmp !== 0) return levelCmp;
      return reportingPickerLabel(a).localeCompare(reportingPickerLabel(b));
    });
  }, [reportingLevelByKey, reportingPickerLabel, reportingTree?.nodes]);

  const subordinatePickerNodes = useMemo(
    () =>
      sortedReportingNodes.filter((n) => {
        if (n.key === subordinateManagerKey) return false;
        if (!hideAlreadyAssignedSubordinates) return true;
        return !n.reports_to_key;
      }),
    [hideAlreadyAssignedSubordinates, sortedReportingNodes, subordinateManagerKey]
  );

  return {
    reportingTree,
    reportingBusy,
    lastReportingRefreshAt,
    refreshReportingTree,
    reportingNodeByKey,
    reportingForest,
    reportingLevelByKey,
    userDirectoryByProfileId,
    externalDirectoryById,
    reportingNodeLabel,
    reportingPickerLabel,
    sortedReportingNodes,
    subordinatePickerNodes,
  };
}
